import { Box, Typography, Paper, Grid, Chip } from "@mui/material";
import { MenuBook, MonetizationOn, Category } from "@mui/icons-material";
import type { ReactNode } from "react";

interface StatEntry {
  title: string;
  value: string;
  subtitle?: string;
  icon: ReactNode;
  iconBg: string;
  typeEntries?: [string, number][];
}

interface InsightStats {
  bookings: StatEntry;
  totalRaised: StatEntry;
  donationTypes: StatEntry & { typeEntries: [string, number][] };
}

interface InsightStatsCardsProps {
  stats: InsightStats;
}

export function buildInsightStats(
  rows: { totalRaised: number; donationCount: number }[],
  allDonations: { type_of_donation_name?: string | null }[],
): InsightStats {
  const bookingsWithDonations = rows.filter((r) => r.donationCount > 0).length;
  const totalBookings = rows.length;
  const totalRaised = rows.reduce((sum, r) => sum + r.totalRaised, 0);

  const typeCounts = new Map<string, number>();
  for (const donation of allDonations) {
    const typeName = donation.type_of_donation_name || "General";
    typeCounts.set(typeName, (typeCounts.get(typeName) || 0) + 1);
  }
  const typeEntries = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1]);
  const totalDonationCount = allDonations.length;
  const typesCount = typeCounts.size;

  return {
    bookings: {
      title: "Bookings with CHEMA",
      value: `${bookingsWithDonations}/${totalBookings}`,
      icon: <MenuBook sx={{ color: "#10b981" }} />,
      iconBg: "#ecfdf5",
    },
    totalRaised: {
      title: "Total CHEMA Raised",
      value: `$${totalRaised.toFixed(2)}`,
      icon: <MonetizationOn sx={{ color: "#f59e0b" }} />,
      iconBg: "#fffbeb",
    },
    donationTypes: {
      title: "Donation Types",
      value: `${typesCount} types`,
      subtitle: `${totalDonationCount} donations`,
      icon: <Category sx={{ color: "#3b82f6" }} />,
      iconBg: "#eff6ff",
      typeEntries,
    },
  };
}

const StatCard = ({ stat }: { stat: StatEntry & { typeEntries?: [string, number][] } }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 4,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      height: "100%",
    }}
  >
    <Box sx={{ flex: 1 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: stat.typeEntries ? 1 : 2 }}>
        {stat.title}
      </Typography>
      <Typography variant="h4" fontWeight="bold">
        {stat.value}
      </Typography>
      {stat.subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {stat.subtitle}
        </Typography>
      )}
      {stat.typeEntries && stat.typeEntries.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
          {stat.typeEntries.map(([name, count]) => (
            <Chip
              key={name}
              label={`${name} (${count})`}
              size="small"
              sx={{ fontSize: "0.7rem", height: 22, bgcolor: "#eff6ff", color: "#1e40af" }}
            />
          ))}
        </Box>
      )}
    </Box>
    <Box
      sx={{
        bgcolor: stat.iconBg,
        p: 1.5,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {stat.icon}
    </Box>
  </Paper>
);

const InsightStatsCards = ({ stats }: InsightStatsCardsProps) => (
  <Grid container spacing={3} sx={{ mb: 4 }}>
    <Grid size={{ xs: 12, md: 4 }}>
      <StatCard stat={stats.bookings} />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <StatCard stat={stats.totalRaised} />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <StatCard stat={stats.donationTypes} />
    </Grid>
  </Grid>
);

export default InsightStatsCards;
